import { chartRecordRemovalReasonLabel } from './chart-record-removal-reasons'
import { useEffect, useState } from 'react'
import { Loader, Modal, Text } from '@mantine/core'
import { IconHistory } from '@tabler/icons-react'
import { useClient, type OrpcClient } from '@/hooks/useClient'
import type { ChartRecordResourceType } from '@/model/middleware-types'
import { formatDateTime } from '@/utils'
import { logError } from '@/utils/errorUtils'

type ActivityEvent = Awaited<ReturnType<OrpcClient['chartRecordActivity']>>['events'][number]

type Props = {
  opened: boolean
  onClose: () => void
  resourceType: ChartRecordResourceType
  resourceId: string | undefined
}

export function ChartRecordActivityModal({ opened, onClose, resourceType, resourceId }: Props) {
  const client = useClient()
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!opened || !resourceId) {
      return
    }

    let active = true
    setLoading(true)
    client
      .chartRecordActivity({ resourceType, resourceId })
      .then((result) => {
        if (active) setEvents(result.events)
      })
      .catch((error) => {
        logError('patient', 'Load chart record activity', error)
        if (active) setEvents([])
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [client, opened, resourceType, resourceId])

  return (
    <Modal opened={opened} onClose={onClose} centered title="Activity" size={420}>
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader size="sm" />
        </div>
      ) : events.length === 0 ? (
        <Text size="sm" c="dimmed">
          No removal or restore activity recorded for this item.
        </Text>
      ) : (
        <div className="flex flex-col gap-3">
          {events.map((event) => (
            <div key={event.id} className="flex items-start gap-2 border-b border-gray-100 pb-2">
              <IconHistory size={16} className="mt-0.5 text-gray-400" />
              <div className="min-w-0 flex-1">
                <Text size="sm" fw={600}>
                  {event.action === 'restored' ? 'Restored' : 'Removed from chart'}
                </Text>
                <Text size="xs" c="dimmed">
                  {formatDateTime(event.recorded, '-', true)}
                  {event.actor ? ` • ${event.actor}` : ''}
                </Text>
                {event.reason && (
                  <Text size="xs" className="mt-1">
                    Reason: {chartRecordRemovalReasonLabel(event.reason)}
                  </Text>
                )}
                {event.explanation && (
                  <Text size="xs" c="dimmed">
                    {event.explanation}
                  </Text>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}
